import { useEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Container,
  FilledInput,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  VisibilityRounded,
  VisibilityOffRounded,
  PersonAddRounded,
  Google,
  HelpRounded
} from '@mui/icons-material'
import Loading from './loading'
import {
  useSignInEmail,
  useSignInGoogle
} from '../hooks/auth'
import { useTheme } from '../providers/theme'
import { useAuth } from '../providers/auth'

export default function Login() {
  const [signInEmail, loadingEmail, errorEmail, clearEmailError] = useSignInEmail()
  const [signInGoogle, loadingGoogle] = useSignInGoogle()
  const {loading} = useAuth()
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [showPasswordButton, setShowPasswordButton] = useState(false)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const passwordRef = useRef<HTMLInputElement>(null)
  const {setMode} = useTheme()

  useEffect(() => {
    setMode('light')
    // eslint-disable-next-line
  }, [])

  const handleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
  }

  const handleSignInEmail = async () => {
    setShowPassword(false)
    await signInEmail(email, password)
  }

  if (loadingGoogle || loading) return <Loading />

  return (
    <div>
    <Container maxWidth="xs">
      <Typography variant="h6" component="h1" my={2}>
        Sign in
      </Typography>

      <FormControl fullWidth variant="filled">
        <InputLabel htmlFor="email" error={!!errorEmail?.email}>Email</InputLabel>
        <FilledInput
          id="email"
          value={email}
          onChange={e => {
            if (errorEmail?.email) clearEmailError()
            setEmail(e.target.value)
          }}
          onKeyUp={e => e.key === 'Enter' && passwordRef.current && passwordRef.current.focus()}
          disabled={loadingEmail}
          error={!!errorEmail?.email}
        />
        {errorEmail?.email && <FormHelperText error>{errorEmail?.email}</FormHelperText>}
      </FormControl>

      <FormControl fullWidth sx={{ mt: 2 }} variant="filled">
        <InputLabel htmlFor="password" error={!!errorEmail?.password}>Password</InputLabel>
        <FilledInput
          ref={passwordRef}
          id="password"
          value={password}
          type={showPassword ? 'text' : 'password'}
          onFocus={() => setShowPasswordButton(true)}
          onBlur={() => setShowPasswordButton(false)}
          onChange={e => {
            if (errorEmail?.password) clearEmailError()
            setPassword(e.target.value)
          }}
          onKeyUp={e => e.key === 'Enter' && handleSignInEmail()}
          disabled={loadingEmail}
          error={!!errorEmail?.password}
          endAdornment={showPasswordButton &&
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleShowPassword}
                onMouseDown={handleMouseDown}
                disabled={loadingEmail}
                edge="end"
              >
                {showPassword ? <VisibilityOffRounded /> : <VisibilityRounded />}
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>

      <Box mt={2}>
        <LoadingButton
          fullWidth
          size="large"
          id="sign-in-button"
          loading={loadingEmail}
          variant="contained"
          disabled={loadingEmail}
          onClick={handleSignInEmail}
        >
          Sign in
        </LoadingButton>
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          marginTop: ".5em"
        }}
      >
        <Link
          underline="hover"
          component={RouterLink}
          to="/forgot"
          sx={{
            cursor: "pointer",
            color: "#85d201",
            transition: "color .5s",
            '&:hover': {
              color: "#538201",
              transition: "color .5s"
            }
          }}
        >
          {"Forgot password?"}
        </Link>
      </Box>
      <Box mt={2}>
        <List disablePadding>
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/register">
              <ListItemIcon>
                <PersonAddRounded />
              </ListItemIcon>
              <ListItemText primary="Sign up" />
            </ListItemButton>
          </ListItem>

          <ListItem
            disablePadding
            onClick={signInGoogle}
          >
            <ListItemButton>
              <ListItemIcon>
                <Google />
              </ListItemIcon>
              <ListItemText primary="Sign in with Google" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton onClick={() => {
              var language = (navigator as any).language || (navigator as any).userLanguage
              const url = `https://webhelp.indexbraille.com/ibwa/${language === 'sv' ? language : 'en-us'}/index.html?introduction.html`
              window.open(url, '_blank')
            }}>
              <ListItemIcon>
                <HelpRounded />
              </ListItemIcon>
              <ListItemText primary="Help" />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        padding: 2
      }}>
        <img
          src="https://firebasestorage.googleapis.com/v0/b/index-braille.appspot.com/o/logotype%2Flogo.svg?alt=media&token=ac10c622-b14e-4424-8089-92cd91087099"
          alt="Index Braille AB"
          style={{ width: 128 }}
        />
      </Box>
    </Container>
    </div>
  )
}
